import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import "../../../../css/forms.css";
import "../../../../css/cards.css";
import "../../../../css/buttons.css";
import "../../../../css/layout.css";

import { api } from "../../../../api/apiClient";
import PageHeader from "../../../../app/layout/PageHeader";
import { notify } from "../../../components-ui/toast";
import AudienceCard from "./components/AudienceCard";
import CampaignDetailsCard from "./components/CampaignDetailsCard";
import MessageCard from "./components/MessageCard";
import PreviewCard from "./components/PreviewCard";
import SendOptionsCard from "./components/SendOptionsCard";
import { useAuth } from "../../../../context/AuthContext";
import {
  Audience,
  AudiencePreview,
  CampaignChannel,
  MessageTemplate,
  RecipientSummary,
  SendOption,
} from "./components/types";

interface CampaignBuilderState {
  campaignId?: number;
  campaignName: string;
  campaignNotes: string;
  channel: CampaignChannel;
  templateId: number | null;
  templates: MessageTemplate[];
  templatesLoading: boolean;
  templatesError: string | null;
  audienceId: number | null;
  audiencePreview: AudiencePreview | null;
  audiences: Audience[];
  message: string;
  recipientSummary: RecipientSummary | null;
  sendOption: SendOption;
  serviceId: number | null;
  serviceInstanceId: number | null;
  serviceCommunicationType: "PreRegistration" | "PostRegistration" | null;
  services: { serviceId: number; name: string }[];
  sessions: { serviceInstanceId: number; serviceId: number; startDateTime: string; endDateTime: string }[];
  scheduledDate: string;
  scheduledTime: string;
  isAudienceLoading: boolean;
  isPreviewLoading: boolean;
  loadingRecipients: boolean;
  savingDraft: boolean;
  audienceError: string | null;
  previewError: string | null;
  saveError: string | null;
}

interface AudiencePreviewResponse {
  audienceId: number;
  totalCount: number;
  members: { partyId: number; displayName: string | null }[];
}

interface SaveDraftRequest {
  name: string;
  channel: CampaignChannel;
  templateId?: number;
  audienceId?: number;
  message: string;
  scheduleType: "now" | "later";
  scheduledDate?: string;
  scheduledTime?: string;
  scheduledAtUtc?: string;
  status: "Draft";
  description?: string;
  serviceInstanceId?: number;
  serviceCommunicationType?: "PreRegistration" | "PostRegistration";
}

interface SaveDraftResponse {
  campaignId: number;
}

interface CampaignResponse {
  campaignId: number;
  name: string;
  description?: string | null;
  channel?: CampaignChannel;
  templateId?: number | null;
  audienceId?: number | null;
  message?: string | null;
  scheduleType?: "now" | "later" | null;
  scheduledDate?: string | null;
  scheduledTime?: string | null;
  scheduledAtUtc?: string | null;
  status: string;
  createdDate: string;
  serviceId?: number | null;
  serviceInstanceId?: number | null;
  serviceCommunicationType?: "PreRegistration" | "PostRegistration" | null;
}

interface CampaignBuilderPageProps {
  campaignId?: number | null;
  onNext?: (campaignId: number) => void;
  onSaved?: (campaignId: number) => void;
  onBack?: () => void;
  initialServiceId?: number | null;
  initialServiceInstanceId?: number | null;
  initialCommunicationType?: "PreRegistration" | "PostRegistration" | null;
}

const tokens = [
  "{{FirstName}}",
  "{{LastName}}",
  "{{FullName}}",
];

export default function CampaignBuilderPage({
  campaignId,
  onNext,
  onSaved,
  onBack,
  initialServiceId,
  initialServiceInstanceId,
  initialCommunicationType,
}: CampaignBuilderPageProps) {
  const { canSendCommunications } = useAuth();
  const [state, setState] = useState<CampaignBuilderState>({
    campaignId: campaignId ?? undefined,
    campaignName: "",
    campaignNotes: "",
    channel: "SMS",
    templateId: null,
    templates: [],
    templatesLoading: false,
    templatesError: null,
    audienceId: null,
    audiencePreview: null,
    audiences: [],
    message: "",
    recipientSummary: null,
    sendOption: "now",
    serviceId: initialServiceId ?? null,
    serviceInstanceId: initialServiceInstanceId ?? null,
    serviceCommunicationType: initialCommunicationType ?? null,
    services: [],
    sessions: [],
    scheduledDate: "",
    scheduledTime: "",
    isAudienceLoading: false,
    isPreviewLoading: false,
    loadingRecipients: false,
    savingDraft: false,
    audienceError: null,
    previewError: null,
    saveError: null,
  });

  const updateState = <K extends keyof CampaignBuilderState>(
    key: K,
    value: CampaignBuilderState[K],
  ) => {
    setState((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    let isMounted = true;

    const loadCampaign = async () => {
      if (!campaignId) {
        setState((prev) => ({
          ...prev,
          campaignId: undefined,
          campaignName: "",
          campaignNotes: "",
          channel: "SMS",
          templateId: null,
          audienceId: null,
          message: "",
          sendOption: "now",
          serviceId: initialServiceId ?? null,
          serviceInstanceId: initialServiceInstanceId ?? null,
          serviceCommunicationType: initialCommunicationType ?? null,
          scheduledDate: "",
          scheduledTime: "",
          recipientSummary: null,
          saveError: null,
        }));
        return;
      }

      try {
        const campaign = await api<CampaignResponse>(
          `/api/campaign/${campaignId}`,
          {
            method: "GET",
          },
        );

        if (!isMounted) return;

        setState((prev) => ({
          ...prev,
          campaignId: campaign.campaignId,
          campaignName: campaign.name,
          campaignNotes: campaign.description ?? "",
          channel: campaign.channel ?? "SMS",
          templateId: campaign.templateId ?? null,
          audienceId: campaign.audienceId ?? null,
          message: campaign.message ?? "",
          sendOption: campaign.scheduleType === "later" ? "schedule" : "now",
          serviceId: campaign.serviceId ?? null,
          serviceInstanceId: campaign.serviceInstanceId ?? null,
          serviceCommunicationType: campaign.serviceCommunicationType ?? null,
          scheduledDate: campaign.scheduledAtUtc
            ? new Date(campaign.scheduledAtUtc).toLocaleDateString("en-CA")
            : (campaign.scheduledDate ?? ""),
          scheduledTime: campaign.scheduledAtUtc
            ? new Date(campaign.scheduledAtUtc).toTimeString().slice(0, 5)
            : (campaign.scheduledTime ?? ""),
          recipientSummary: null,
          saveError: null,
        }));
      } catch {
        if (!isMounted) return;

        notify.error("Failed to load campaign");
      }
    };

    loadCampaign();

    return () => {
      isMounted = false;
    };
  }, [campaignId, initialCommunicationType, initialServiceId, initialServiceInstanceId]);

  useEffect(() => {
    let active = true;
    api<{ serviceId: number; name: string }[]>("/api/ServicesCalendar/available-services", { suppressLoading: true })
      .then((services) => active && setState((prev) => ({ ...prev, services })))
      .catch(() => active && notify.error("Failed to load services"));
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!state.serviceId) {
      setState((prev) => ({ ...prev, sessions: [] }));
      return;
    }
    let active = true;
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - 1);
    const toDate = new Date();
    toDate.setFullYear(toDate.getFullYear() + 1);
    api<{ serviceInstanceId: number; serviceId: number; startDateTime: string; endDateTime: string }[]>(
      "/api/ServicesCalendar/search",
      {
        method: "POST",
        body: { serviceIds: [state.serviceId], fromDate: fromDate.toISOString(), toDate: toDate.toISOString() },
        suppressLoading: true,
      },
    ).then((sessions) => active && setState((prev) => ({ ...prev, sessions })))
      .catch(() => active && notify.error("Failed to load service sessions"));
    return () => { active = false; };
  }, [state.serviceId]);

  useEffect(() => {
    let isMounted = true;

    const loadTemplates = async () => {
      if (state.channel !== "WHATSAPP") return;

      setState((prev) => ({
        ...prev,
        templatesLoading: true,
        templatesError: null,
      }));

      try {
        const templates = await api<MessageTemplate[]>(
          "/api/campaign/templates?channelType=WHATSAPP",
        );
        if (!isMounted) return;

        setState((prev) => ({
          ...prev,
          templates,
          templatesLoading: false,
        }));
      } catch {
        if (!isMounted) return;
        setState((prev) => ({
          ...prev,
          templates: [],
          templatesLoading: false,
          templatesError: "Failed to load approved WhatsApp templates",
        }));
      }
    };

    loadTemplates();
    return () => {
      isMounted = false;
    };
  }, [state.channel]);

  useEffect(() => {
    let isMounted = true;

    const loadAudiences = async () => {
      setState((prev) => ({
        ...prev,
        isAudienceLoading: true,
        audienceError: null,
      }));

      try {
        const audiences = await api<Audience[]>("/api/audience");

        if (!isMounted) return;

        setState((prev) => ({
          ...prev,
          audiences,
          isAudienceLoading: false,
        }));
      } catch {
        if (!isMounted) return;

        setState((prev) => ({
          ...prev,
          audiences: [],
          isAudienceLoading: false,
          audienceError: "Failed to load audiences",
        }));
      }
    };

    loadAudiences();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadAudiencePreview = async () => {
      if (!state.audienceId) {
        setState((prev) => ({
          ...prev,
          audiencePreview: null,
          previewError: null,
          isPreviewLoading: false,
        }));
        return;
      }

      setState((prev) => ({
        ...prev,
        isPreviewLoading: true,
        previewError: null,
      }));

      try {
        const preview = await api<AudiencePreviewResponse>(
          `/api/audience/${state.audienceId}/preview`,
        );

        if (!isMounted) return;

        setState((prev) => ({
          ...prev,
          audiencePreview: {
            staticMembers: 0,
            ruleMembers: 0,
            total: preview.totalCount,
          },
          isPreviewLoading: false,
        }));
      } catch {
        if (!isMounted) return;

        setState((prev) => ({
          ...prev,
          audiencePreview: null,
          isPreviewLoading: false,
          previewError: "Failed to preview audience",
        }));
      }
    };

    loadAudiencePreview();

    return () => {
      isMounted = false;
    };
  }, [state.audienceId]);

  const previewRecipients = async () => {
    if (state.loadingRecipients) return;

    if (!state.audienceId) {
      notify.error("Please select a contact list");
      return;
    }

    if (state.channel === "WHATSAPP" && !state.templateId) {
      notify.error("Please select an approved WhatsApp template");
      return;
    }

    if (state.channel === "SMS" && !state.message.trim()) {
      notify.error("Please enter a message");
      return;
    }

    setState((prev) => ({
      ...prev,
      loadingRecipients: true,
      recipientSummary: null,
    }));

    try {
      const summary = await api<RecipientSummary>("/api/campaign/preview", {
        method: "POST",
        body: JSON.stringify({
          audienceId: state.audienceId,
          message: state.message,
          channel: state.channel,
          templateId: state.templateId,
        }),
      });

      setState((prev) => ({
        ...prev,
        recipientSummary: summary,
      }));
    } catch {
      notify.error("Failed to run recipient check");
    } finally {
      setState((prev) => ({
        ...prev,
        loadingRecipients: false,
      }));
    }
  };

  const buildDraftRequest = (): SaveDraftRequest => {
    const isScheduled = state.sendOption === "schedule";
    const scheduledAtUtc =
      isScheduled && state.scheduledDate && state.scheduledTime
        ? new Date(
            `${state.scheduledDate}T${state.scheduledTime}`,
          ).toISOString()
        : undefined;

    return {
      name: state.campaignName,
      channel: state.channel,
      templateId: state.templateId ?? undefined,
      audienceId: state.audienceId ?? undefined,
      message: state.message,
      scheduleType: isScheduled ? "later" : "now",
      scheduledDate: state.scheduledDate || undefined,
      scheduledTime: state.scheduledTime || undefined,
      scheduledAtUtc,
      status: "Draft",
      description: state.campaignNotes.trim() || undefined,
      serviceInstanceId: state.serviceInstanceId ?? undefined,
      serviceCommunicationType: state.serviceInstanceId
        ? state.serviceCommunicationType ?? undefined
        : undefined,
    };
  };

  const saveDraft = async (): Promise<number | null> => {
    if (state.savingDraft) return null;

    if (!state.campaignName.trim()) {
      notify.error("Campaign name is required");
      return null;
    }

    const isPostRegistration = state.serviceCommunicationType === "PostRegistration";
    if (!state.audienceId && !isPostRegistration) {
      notify.error("Please select a contact list");
      return null;
    }

    if (state.serviceInstanceId && !state.serviceCommunicationType) {
      notify.error("Please select pre-registration or post-registration");
      return null;
    }

    if (state.channel === "WHATSAPP" && !state.templateId) {
      notify.error("Please select an approved WhatsApp template");
      return null;
    }

    if (state.channel === "SMS" && !state.message.trim()) {
      notify.error("Please enter a message");
      return null;
    }

    if (
      state.sendOption === "schedule" &&
      (!state.scheduledDate || !state.scheduledTime)
    ) {
      notify.error("Please select a scheduled date and time");
      return null;
    }

    if (
      state.sendOption === "schedule" &&
      new Date(`${state.scheduledDate}T${state.scheduledTime}`) <= new Date()
    ) {
      notify.error("Scheduled date and time must be in the future");
      return null;
    }

    setState((prev) => ({
      ...prev,
      savingDraft: true,
      saveError: null,
    }));

    try {
      const isEditing = Boolean(state.campaignId);
      const campaign = await api<SaveDraftResponse>(
        isEditing ? `/api/campaign/${state.campaignId}` : "/api/campaign",
        {
          method: isEditing ? "PUT" : "POST",
          body: JSON.stringify(buildDraftRequest()),
        },
      );

      setState((prev) => ({
        ...prev,
        campaignId: campaign.campaignId,
      }));

      onSaved?.(campaign.campaignId);
      notify.success("Draft saved");
      return campaign.campaignId;
    } catch {
      setState((prev) => ({
        ...prev,
        saveError: "Failed to save draft",
      }));
      notify.error("Failed to save draft");
      return null;
    } finally {
      setState((prev) => ({
        ...prev,
        savingDraft: false,
      }));
    }
  };

  const saveAndReview = async () => {
    const savedCampaignId = await saveDraft();

    if (!savedCampaignId) return;

    onNext?.(savedCampaignId);
  };

  const insertToken = (token: string) => {
    setState((prev) => ({
      ...prev,
      message: prev.message ? `${prev.message} ${token}` : token,
    }));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  };

  const handleCancel = () => {
    if (state.campaignId) {
      const confirmed = window.confirm(
        "Discard changes?\n\nAny unsaved changes will be lost.",
      );

      if (!confirmed) return;
    }

    onBack?.();
  };

  return (
    <form className="page-container" onSubmit={handleSubmit}>
      <PageHeader
        title="Campaign Builder"
        subtitle="Create, preview and schedule a communication campaign."
        actions={
          <button
            className="icon-button-solid icon-button-solid--danger"
            onClick={handleCancel}
            title="Cancel"
            type="button"
          >
            <CloseIcon fontSize="small" />
          </button>
        }
      />

      {/* SECTION 1: Campaign Details */}
      <CampaignDetailsCard
        campaignName={state.campaignName}
        campaignNotes={state.campaignNotes}
        channel={state.channel}
        onChannelChange={(channel) =>
          setState((prev) => ({
            ...prev,
            channel,
            templateId: null,
            message: "",
            recipientSummary: null,
          }))
        }
        onCampaignNameChange={(value) => updateState("campaignName", value)}
        onCampaignNotesChange={(value) => updateState("campaignNotes", value)}
      />

      <section className="card campaign-session-card">
        <div className="card-header-row">
          <div>
            <div className="card-title">Service Session</div>
            <p className="field-hint">Optional. Link this campaign to one specific calendar session.</p>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Service</label>
            <select className="form-input" value={state.serviceId ?? ""} onChange={(event) => setState((prev) => ({ ...prev, serviceId: Number(event.target.value) || null, serviceInstanceId: null, serviceCommunicationType: null }))}>
              <option value="">No service</option>
              {state.services.map((service) => <option key={service.serviceId} value={service.serviceId}>{service.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Session</label>
            <select className="form-input" disabled={!state.serviceId} value={state.serviceInstanceId ?? ""} onChange={(event) => setState((prev) => ({ ...prev, serviceInstanceId: Number(event.target.value) || null, serviceCommunicationType: Number(event.target.value) ? prev.serviceCommunicationType : null }))}>
              <option value="">Select a session</option>
              {state.sessions.map((session) => <option key={session.serviceInstanceId} value={session.serviceInstanceId}>{new Date(session.startDateTime).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Communication</label>
            <select className="form-input" disabled={!state.serviceInstanceId} value={state.serviceCommunicationType ?? ""} onChange={(event) => updateState("serviceCommunicationType", (event.target.value || null) as CampaignBuilderState["serviceCommunicationType"])}>
              <option value="">Select a type</option>
              <option value="PreRegistration">Pre-registration</option>
              <option value="PostRegistration">Post-registration auto reply</option>
            </select>
          </div>
        </div>
      </section>

      {/* SECTION 2: Audience Selection */}
      <AudienceCard
        audiences={state.audiences}
        selectedAudienceId={state.audienceId}
        preview={state.audiencePreview}
        audienceLoading={state.isAudienceLoading}
        loading={state.isPreviewLoading}
        error={state.audienceError ?? state.previewError}
        onAudienceChange={(value) =>
          setState((prev) => ({
            ...prev,
            audienceId: value,
            audiencePreview: null,
            previewError: null,
          }))
        }
      />

      {/* SECTION 3: Message Builder */}
      <MessageCard
        value={state.message}
        channel={state.channel}
        templates={state.templates}
        selectedTemplateId={state.templateId}
        templatesLoading={state.templatesLoading}
        templatesError={state.templatesError}
        tokens={tokens}
        onChange={(value) => updateState("message", value)}
        onTokenClick={insertToken}
        onTemplateChange={(templateId) => {
          const template = state.templates.find(
            (item) => item.messageTemplateId === templateId,
          );
          setState((prev) => ({
            ...prev,
            templateId,
            message: template?.content ?? "",
            recipientSummary: null,
          }));
        }}
      />

      {/* SECTION 4: Preview & Recipient Check */}
      <PreviewCard
        channel={state.channel}
        data={state.recipientSummary}
        loading={state.loadingRecipients}
        onRunCheck={previewRecipients}
      />

      {/* SECTION 5: Send Options */}
      {canSendCommunications ? <SendOptionsCard
        sendOption={state.sendOption}
        scheduledDate={state.scheduledDate}
        scheduledTime={state.scheduledTime}
        onSendOptionChange={(value) => updateState("sendOption", value)}
        onScheduledDateChange={(value) => updateState("scheduledDate", value)}
        onScheduledTimeChange={(value) => updateState("scheduledTime", value)}
      /> : <div className="card field-hint">You can prepare this campaign as a draft. Sending and scheduling require Service Manager and Communications Sender roles, plus access to the service.</div>}

      {/* SECTION 6: Actions */}
      <div className="form-actions">
        {onBack && (
          <button type="button" className="form-submit-button" onClick={onBack}>
            Back
          </button>
        )}
        <button
          type="button"
          className="form-submit-button"
          onClick={saveDraft}
          disabled={state.savingDraft}
        >
          {state.savingDraft ? "Saving..." : "Save Draft"}
        </button>
        <button
          type="button"
          className="form-submit-button"
          onClick={saveAndReview}
          disabled={state.savingDraft}
        >
          {onNext ? "Next" : "Send Campaign"}
        </button>
      </div>
      {state.saveError && <div className="form-error">{state.saveError}</div>}
    </form>
  );
}
